<template>
    <div class="geoproceso-form form-section">
        <!-- HERRAMIENTAS DE DIBUJO -->
        <label>Ubique un punto:</label>
        <button :class="['draw-btn', { 'draw-btn--active': drawActive }]" @click="drawPoint">●</button>

        <!-- Tiempo y velocidad -->
        <div class="inputs-row">
            <div>
                <label style="font-size: 12px">Tiempo desarrollo pluma (h):</label>
                <input
                    type="number"
                    :value="pluma.tiempo"
                    min="1"
                    max="5"
                    step="1"
                    style="width: 60px;"
                    @keydown="bloquearTeclasInvalidas"
                    @input="actualizarCampoPluma('tiempo', $event)"
                    @blur="restaurarCampoVacio('tiempo', $event)"
                />
            </div>
            <div>
                <label style="font-size: 12px">Velocidad del viento (km/h):</label>
                <input
                    type="number"
                    :value="pluma.velocidad"
                    min="1"
                    max="100"
                    step="1"
                    style="width: 70px;"
                    @keydown="bloquearTeclasInvalidas"
                    @input="actualizarCampoPluma('velocidad', $event)"
                    @blur="restaurarCampoVacio('velocidad', $event)"
                />
            </div>
        </div>

        <!-- Dirección del viento -->
        <div class="direccion">
            <label style="font-size: 12px; font-weight: 600;">Dirección del viento (°)</label>
            <div class="wind-row">
                <button class="btn-small" @click="cambiarDireccion(-1)">−</button>
                <input class="slider" type="range" v-model.number="pluma.direccion" min="0" max="359" />
                <button class="btn-small" @click="cambiarDireccion(1)">+</button>
                <div class="brujula">
                    <span class="cardinal cardinal-n">N</span>
                    <span class="cardinal cardinal-e">E</span>
                    <span class="cardinal cardinal-s">S</span>
                    <span class="cardinal cardinal-o">O</span>
                    <div class="aguja" :style="{ transform: 'translateX(-50%) rotate(' + pluma.direccion + 'deg)' }"></div>
                </div>
                <span class="deg">{{ pluma.direccion }}°</span>
            </div>
        </div>

        <GeoprocesoOpcionesGenerales
            :capas.sync="capas"
            :metodo.sync="metodo"
            :capasSeleccionadas.sync="capasSeleccionadas"
            :dissolve.sync="dissolve"
            :show-advanced="false"
        />
    </div>
</template>

<script>
import GeoprocesoBaseMixin from '@/components/mapTools/geoprocessings/mixins/GeoprocesoBaseMixin';
import GeoprocesoOpcionesGenerales from '@/components/mapTools/geoprocessings/forms/GeoprocesoOpcionesGenerales';

// Los min/max de un input type="number" solo limitan las flechitas del
// spinner: escribiendo o pegando igual entran decimales, negativos, notación
// científica o números gigantes. Los campos se acotan también en el handler.
const LIMITES_PLUMA = {
    tiempo: { min: 1, max: 5 },
    velocidad: { min: 1, max: 100 },
};

export default {
    name: 'PlumaDispersionForm',
    components: { GeoprocesoOpcionesGenerales },
    mixins: [GeoprocesoBaseMixin],
    data() {
        return {
            pluma: {
                tiempo: 1,
                velocidad: 8,
                direccion: 0,
            },
            drawActive: false,
        };
    },
    watch: {
        pluma: {
            handler() {
                this.emitState();
                this.scheduleGeneratePlume();
            },
            deep: true,
        },
    },
    methods: {
        drawPoint() {
            this.drawActive = true;
            this.$emit('changeDrawType', { name: 'Punto', code: 'Point', once: true });
        },
        cambiarDireccion(delta) {
            this.pluma.direccion = (this.pluma.direccion + delta + 360) % 360;
        },
        bloquearTeclasInvalidas(e) {
            // solo enteros positivos: sin signo, decimales ni notación científica
            if (['e', 'E', '+', '-', '.', ','].includes(e.key)) e.preventDefault();
        },
        actualizarCampoPluma(campo, e) {
            const { min, max } = LIMITES_PLUMA[campo];
            const bruto = e.target.value;
            // vacío mientras se edita: se permite y el guard del debounce
            // evita pedir la pluma hasta tener un valor válido
            if (bruto === '') {
                this.pluma[campo] = '';
                return;
            }
            const n = Math.trunc(Number(bruto));
            const acotado = isFinite(n) ? Math.min(max, Math.max(min, n)) : min;
            this.pluma[campo] = acotado;
            // si se acotó y el estado no cambió, Vue no vuelve a renderizar:
            // se corrige el input directo para que no quede mostrando otra cosa
            if (String(acotado) !== bruto) e.target.value = String(acotado);
        },
        restaurarCampoVacio(campo, e) {
            if (typeof this.pluma[campo] === 'number') return;
            const { min } = LIMITES_PLUMA[campo];
            this.pluma[campo] = min;
            e.target.value = String(min);
        },
        // La pluma se genera automáticamente al cambiar cualquier parámetro.
        // Como el cálculo lo resuelve un servicio remoto, se aplica un debounce
        // para no dispararlo en cada paso del slider mientras se arrastra.
        scheduleGeneratePlume() {
            clearTimeout(this._plumeTimer);
            this._plumeTimer = setTimeout(() => {
                // Mientras se edita un input puede quedar vacío (""): no
                // pedimos la pluma hasta tener valores válidos, si no el
                // servicio responde 422.
                const { tiempo, velocidad, direccion } = this.pluma;
                if (!(tiempo >= 1) || !(velocidad >= 1)) return;
                this.$emit('generatePlume', {
                    hours: tiempo,
                    windDirection: direccion,
                    windSpeed: velocidad,
                });
            }, 350);
        },
        currentPayload() {
            return {
                ...this.getCommonPayload(),
                pluma: this.pluma,
            };
        },
    },
    beforeDestroy() {
        clearTimeout(this._plumeTimer);
    },
};
</script>

<style src="./_geoproceso-forms.css"></style>
<style scoped>
.wind-row { display: flex; align-items: center; gap: 8px; margin-top: 5px; }
.deg { width: 35px; font-size: 12px; }
</style>
